<template>
  <div
    id="slider"
    class="relative gap-2 grid grid-cols-12 mt-[10%] w-full lg:h-[85svh] max-md:min-h-svh column-gap"
  >
    <!-- For larger devices, show one person at a time with index -->
    <template v-if="!isSmallScreen">
      <div
        class="relative flex flex-col col-span-full columns-gap lg:h-full max-lg:h-fit"
      >
        <div>
          <p
            id="quote-text"
            class="mb-14 md:max-w-full min-h-36 md:min-h-fit lg:min-h-36 font-semibold md:leading-none lg:leading-normal heading-3"
            v-html="computedQuote"
          ></p>
          <div id="quote-author" class="mb-6 font-semibold heading-6">
            <p>{{ people[index].author }}</p>
            <p class="text-flax-smoke-400">{{ people[index].position }}</p>
          </div>
          <div id="quote-tags" class="flex gap-3">
            <p
              class="px-3 border border-flax-smoke-500 rounded-full text-flax-smoke-600 uppercase"
              v-for="i in people[index].tags"
              :key="i"
            >
              {{ i }}
            </p>
          </div>
        </div>

        <div class="relative flex justify-between items-end h-full">
          <div class="flex items-center gap-3 w-2/12 overflow-clip heading-5">
            <p
              id="current-index"
              class="-translate-y-full will-change-transform"
            >
              {{ index + 1 }}
            </p>
            <p class="bg-black w-full h-0.5"></p>
            <p>{{ people.length }}</p>
          </div>
          <div
            class="lg:-bottom-10 lg:absolute lg:inset-0 lg:w-full lg:will-change-scroll"
          >
            <div
              class="top-[90%] sticky flex place-content-end gap-3 lg:will-change-scroll"
            >
              <Button label="Prev" @click="clickPrev" />
              <Button label="Next" @click="clickNext" />
            </div>
          </div>
        </div>
      </div>
      <!-- <div
        class="relative flex justify-center items-start order-first max-sm:order-last lg:order-last col-span-full lg:col-span-6 columns-gap w-full h-[60vh] lg:h-full overflow-clip"
      >
      <img
          :class="{ hidden: index !== 0 }"
          class="z-10 relative brightness-90 grayscale rounded-lg lg:h-[85svh] size-full object-center object-cover mix-blend-screen"
          :src="people[0].profile"
          alt=""
        />
        <img
          :class="{ hidden: index !== 1 }"
          class="z-10 relative brightness-90 grayscale rounded-lg lg:h-[85svh] size-full object-center object-cover mix-blend-screen"
          :src="people[1].profile"
          alt=""
        />
        <div
          id="quote-overlay"
          class="z-50 absolute inset-0 bg-flax-smoke-500 rounded-lg"
        ></div>
      </div> -->
    </template>

    <!-- For smaller devices, show all people at once -->
    <template v-else>
      <div class="col-span-full">
        <template v-for="(p, i) in people" :key="i">
          <div class="items-start grid grid-cols-5 sm:grid-cols-4 mt-10 w-full">
            <div
              class="relative flex flex-col col-span-1 columns-gap h-full font-bold leading-none heading-2"
            >
              {{ (i + 1).toString().padStart(2, '0') }}
            </div>
            <div class="col-span-3">
              <div class="flex flex-col gap-y-4 columns-gap w-full">
                <!-- <img
                  class="brightness-90 grayscale rounded-md size-full object-center object-cover aspect-square mix-blend-screen"
                  :src="p.profile"
                  alt=""
                /> -->

                <p
                  class="mt-4 max-w-[25ch] font-semibold leading-none heading-4"
                >
                  " {{ p.quote }} "
                </p>

                <div class="mt-4 font-semibold heading-6">
                  <p class="">{{ p.author }}</p>
                  <p class="text-flax-smoke-400">
                    {{ p.position }}
                  </p>
                </div>

                <div
                  class="flex flex-wrap gap-3 max-w-60 uppercase leading-[200%]"
                >
                  <p
                    class="px-3 border border-flax-smoke-500 rounded-full text-flax-smoke-600 uppercase text-nowrap"
                    v-for="i in p.tags"
                    :key="i"
                  >
                    {{ i }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">

  import { Button } from '../common';
  import { computed, onMounted, ref } from 'vue';
  import { useWindowSize } from '@vueuse/core';
  import { textSplitterIntoChar } from '@/functions';
  import gsap from 'gsap';

  const { width } = useWindowSize();
  const isSmallScreen = computed(() => width.value < 640);
  const computedQuote = computed(() => {
    return textSplitterIntoChar(`" ${people[index.value].quote} "`);
  });

  const canClick = ref(true);

  const animateTextTransition = (direction: 'up' | 'zero') => {
    const translateY = direction === 'up' ? '-100%' : '0%';
    gsap.to('#quote-text .letters', {
      translateY,
      duration: 0.5,
      stagger: 0.001,
      ease: 'power1.inOut',
    });
  };

  const animateQuoteAuthorTransition = (
    direction: 'left' | 'right',
    onCompleteFunc?: () => void,
  ) => {
    const translateX = direction === 'left' ? '-50%' : '0%';
    const opacity = direction === 'left' ? 0 : 1;
    gsap.to(['#quote-author', '#quote-tags'], {
      translateX,
      opacity,
      duration: 0.5,
      ease: 'power1.inOut',
      onComplete: () => {
        if (onCompleteFunc) onCompleteFunc();
      },
    });
  };

  const animateCurrentQuoteIndex = (
    direction: 'up' | 'zero',
    onCompleteFunc?: () => void,
  ) => {
    const translateY = direction === 'up' ? '-100%' : '0%';
    gsap.to(['#current-index'], {
      translateY,
      duration: 0.5,
      ease: 'power1.inOut',
      onComplete: () => {
        if (onCompleteFunc) onCompleteFunc();
      },
    });
  };

  const animateQuoteOverlay = (
    newIndex: number,
    onCompleteFunc?: () => void,
  ) => {
    // const translateY = direction === 'up' ? '100%' : '-100%';
    gsap.to('#quote-overlay', {
      translateY: '0%',
      duration: 0.7,
      ease: 'power4.inOut',
      onComplete: () => {
        index.value = newIndex;
        if (onCompleteFunc) onCompleteFunc();

        gsap.to('#quote-overlay', {
          translateY: '-100%',
          duration: 0.7,
          ease: 'power4.inOut',
          onComplete: () => {
            gsap.set('#quote-overlay', { translateY: '100%' });

            canClick.value = true;
          },
        });
      },
    });
  };

  // Function to trigger the quote change
  const changeQuote = (newIndex: number) => {
    animateTextTransition('up');
    animateQuoteAuthorTransition('left');
    animateQuoteOverlay(newIndex, () => {
      setTimeout(() => {
        animateTextTransition('zero');
      }, 25);
      animateCurrentQuoteIndex('zero');
      animateQuoteAuthorTransition('right');
    });
    animateCurrentQuoteIndex('up', () => {
      gsap.set(['#current-index'], {
        y: '100%',
      });
    });
  };

  // Event handlers for next and previous clicks
  const clickNext = () => {
    if (!canClick.value) return;

    canClick.value = false;
    let newIndex = (index.value + 1) % people.length;
    if (newIndex < people.length) changeQuote(newIndex);
  };

  const clickPrev = () => {
    if (!canClick.value) return;

    canClick.value = false;
    const newIndex = (index.value - 1 + people.length) % people.length;
    changeQuote(newIndex);
  };

  onMounted(() => {
    if (!isSmallScreen.value) {
      gsap.set(['#quote-text .letters', '#current-index'], {
        translateY: 0,
      });
      gsap.set('#quote-overlay', {
        translateY: '100%',
      });
    }
  });

  // data
  const index = ref(0);
  const people = [
    {
      quote:
        'I had the pleasure of managing John on several WordPress projects, and he consistently brought reliability, professionalism, and strong problem-solving skills to the table. He handled both frontend and backend development with care, met deadlines, and produced high-quality work that helped move our projects forward. Any team would be lucky to have him.',
      author: 'Lauren Vicenzi',
      position: 'Creative Digital Project Manager at Turkois',
      tags: '',
      // profile: ,
    },
    {
      quote:
        `John is a skilled PHP developer with a strong technical mindset. During his time working with us, he tackled complex coding challenges and contributed solid solutions to our web development projects. His ability to write efficient PHP code was a real asset to the team.

He is highly detail-oriented and brings a unique perspective to problem-solving. While projects often come with communication challenges, John was always committed to delivering quality work. I have no doubt he will continue to refine his skills and make valuable contributions wherever he goes.`,
      author: 'Stuart Farrel',
      position: 'Founder & Managing Director - Milk Digital',
      tags:'',
      // profile: ,
    },
        {
      quote:
        `John is an very experienced PHP and WordPress senior developer with the ability to independently research and resolve complex backend issues. He would be an asset to any team.`,
      author: 'John Hendrix',
      position: 'Director of Web Development at Amplify360',
      tags: '',
      // profile: ,
    },
  ];
</script>
